import { useEffect, useState } from 'react';
import {
  BRANDING_COLORS,
  BRANDING_FONTS,
  brandingSchema,
  type BrandingConfig,
  type BrandingState,
} from '../../shared/branding';
import type { ClientSession } from '../api';
import { brandingApi, useBranding } from '../branding';
import { ErrorMessage, Loading } from '../components/Status';
const fields: {
  key: 'siteName' | 'calendarTagline' | 'loginHeading' | 'loginDescription' | 'eventBackLabel';
  label: string;
  max: number;
}[] = [
  { key: 'siteName', label: 'Site name', max: 80 },
  { key: 'calendarTagline', label: 'Calendar tagline', max: 200 },
  { key: 'loginHeading', label: 'Sign-in heading', max: 100 },
  { key: 'loginDescription', label: 'Sign-in description', max: 300 },
  { key: 'eventBackLabel', label: 'Event back link', max: 60 },
];
export function BrandingPage({ session }: { session: ClientSession | null }) {
  const [state, setState] = useState<BrandingState | null>(null);
  const [values, setValues] = useState<BrandingConfig | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [eventPath, setEventPath] = useState<string | null>(null);
  const { preview: activePreview, setPreview, refresh } = useBranding();
  const allowed = !!session?.capabilities.superAdmin;
  const load = async () => {
    setError('');
    try {
      const result = await brandingApi();
      setState(result);
      setValues(result.draft);
      setConfirmed(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load branding');
    }
  };
  useEffect(() => {
    if (allowed) void load();
  }, [allowed]);
  useEffect(() => {
    if (!allowed) return;
    const from = new Date();
    from.setMonth(from.getMonth() - 1);
    const to = new Date();
    to.setTime(from.getTime() + 365 * 86400000);
    void fetch(
      `/api/events?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
    )
      .then((r) => r.json())
      .then((result: { events?: { path?: string }[] }) => {
        const path = result.events?.find((event) => event.path?.startsWith('/events/'))?.path;
        if (path) setEventPath(path);
      })
      .catch(() => {});
  }, [allowed]);
  if (!allowed)
    return (
      <main id="main-content" className="center-page">
        <h1>Branding & wording</h1>
        <p>Super Admin access is required.</p>
      </main>
    );
  const changed = !!state && !!values && JSON.stringify(state.draft) !== JSON.stringify(values);
  const previewMatches =
    !!state && !!activePreview && JSON.stringify(activePreview) === JSON.stringify(state.draft);
  const mutate = async (action: 'save' | 'publish' | 'rollback') => {
    if (!state || !values) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await brandingApi(
        `/api/admin/branding${action === 'save' ? '' : `/${action}`}`,
        action === 'save' ? 'PATCH' : 'POST',
        {
          version: state.version,
          draftVersion: state.draftVersion,
          ...(action === 'save' ? { config: values } : { confirm: confirmed }),
        },
        session?.csrfToken,
      );
      setState(result);
      setValues(result.draft);
      setConfirmed(false);
      setPreview(null);
      if (action !== 'save') {
        await refresh();
      }
      setNotice(
        action === 'save'
          ? 'Draft saved. Public pages are unchanged.'
          : action === 'publish'
            ? 'Branding published.'
            : 'Previous branding restored.',
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save branding');
    } finally {
      setBusy(false);
    }
  };
  const preview = () => {
    if (busy || changed) {
      setError('Save draft before previewing.');
      return false;
    }
    const result = brandingSchema.safeParse(values);
    if (!result.success) {
      setError('Complete all wording fields with plain text.');
      return false;
    }
    setError('');
    setPreview(result.data);
    setNotice('Preview is active. Follow the preview links to inspect each page.');
    return true;
  };
  return (
    <main id="main-content" className="settings-page branding-page">
      <a href="/admin">Back to administration</a>
      <h1>Branding & wording</h1>
      <p>
        Choose shared colors, fonts and wording. Preview your draft before publishing it for
        everyone.
      </p>
      {error && <ErrorMessage message={error} retry={() => void load()} />}
      <p role="status">{notice}</p>
      {!values || !state ? (
        !error && <Loading />
      ) : (
        <>
          <div className="branding-fields">
            <label className="field">
              <span>Color palette</span>
              <select
                value={values.colorPreset}
                onChange={(e) => {
                  setValues({
                    ...values,
                    colorPreset: e.target.value as BrandingConfig['colorPreset'],
                  });
                  setConfirmed(false);
                }}
              >
                {Object.entries(BRANDING_COLORS).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Typography</span>
              <select
                value={values.fontPreset}
                onChange={(e) => {
                  setValues({
                    ...values,
                    fontPreset: e.target.value as BrandingConfig['fontPreset'],
                  });
                  setConfirmed(false);
                }}
              >
                {Object.entries(BRANDING_FONTS).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.label}
                  </option>
                ))}
              </select>
            </label>
            {fields.map((field) => (
              <label className="field" key={field.key}>
                <span>{field.label}</span>
                <input
                  maxLength={field.max}
                  value={values[field.key]}
                  onChange={(e) => {
                    setValues({ ...values, [field.key]: e.target.value });
                    setConfirmed(false);
                  }}
                  required
                />
              </label>
            ))}
          </div>
          <div className="branding-actions">
            <button
              type="button"
              className="button button-secondary"
              disabled={busy || changed}
              onClick={preview}
            >
              Preview draft
            </button>
            <button
              type="button"
              className="button"
              disabled={busy || !changed}
              onClick={() => void mutate('save')}
            >
              Save draft
            </button>
          </div>
          <nav aria-label="Branding preview pages" className="branding-preview-links">
            <a
              href={changed ? undefined : '/'}
              aria-disabled={changed || busy}
              onClick={(event) => {
                if (!preview()) event.preventDefault();
              }}
            >
              Preview calendar
            </a>
            {eventPath ? (
              <a
                href={changed ? undefined : eventPath}
                aria-disabled={changed || busy}
                onClick={(event) => {
                  if (!preview()) event.preventDefault();
                }}
              >
                Preview event page
              </a>
            ) : (
              <span>Open an event from the calendar to preview its page.</span>
            )}
            <a
              href={changed ? undefined : '/login'}
              aria-disabled={changed || busy}
              onClick={(event) => {
                if (!preview()) event.preventDefault();
              }}
            >
              Preview sign-in
            </a>
          </nav>
          <section className="branding-publish">
            <h2>Publish for everyone</h2>
            <p>
              Published version {state.version}. Save your draft and inspect the pages before
              publishing. Rollback restores the previous published colors, fonts and wording.
            </p>
            <label>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />{' '}
              I have reviewed the preview and confirm this will change the public site.
            </label>
            <div className="branding-actions">
              <button
                className="button"
                type="button"
                disabled={busy || changed || !confirmed || !previewMatches}
                onClick={() => void mutate('publish')}
              >
                Publish branding
              </button>
              <button
                className="button button-secondary"
                type="button"
                disabled={busy || changed || !confirmed || !state.previous}
                onClick={() => void mutate('rollback')}
              >
                Restore previous version
              </button>
            </div>
            {changed && <p>Save draft before previewing or publishing.</p>}
            {!changed && !previewMatches && <p>Preview this saved draft before publishing.</p>}
          </section>
        </>
      )}
    </main>
  );
}
